import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Zap, Layers, Award, GraduationCap, Bot, ArrowRight,
  Terminal, LineChart, Users, Check, Rocket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LANDING, NAV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Bot, title: "Personal Mentor", desc: "A mentor that teaches, adapts, and remembers your progress across every lesson.", tone: "primary" },
  { icon: Layers, title: "Adaptive Lessons", desc: "Every lesson is personalised with objectives, theory, examples, exercises, and a mini-quiz.", tone: "accent" },
  { icon: Terminal, title: "Interactive Coding Lab", desc: "Write, run, and get code reviews with hints instead of just answers.", tone: "primary" },
  { icon: LineChart, title: "Progress Mastery", desc: "Skill mastery, weak/strong topics, streaks, and adaptive difficulty.", tone: "accent" },
  { icon: Award, title: "Verifiable Certificates", desc: "Beautiful shareable certificates with a public verification page.", tone: "primary" },
  { icon: Users, title: "Business & Teams", desc: "Assign courses, track compliance, and lead workforce upskilling.", tone: "accent" },
];

const PATHS_PREVIEW = [
  "Python", "React", "SQL", "AI & ML", "Prompt Engineering",
  "Cybersecurity", "MATLAB", "Power BI", "Leadership", "Interview Prep",
];

const PRICING = [
  { name: "Free", price: "$0", desc: "Try the mentor & 2 paths", features: ["Mentor (25 messages/day)", "2 learning paths", "Basic progress", "Community support"], cta: "Start free", highlight: false },
  { name: "Pro", price: "$19", tag: "/mo", desc: "Unlimited mentorship", features: ["Unlimited Mentor sessions", "All learning paths", "Certificates", "Career Coach", "Coding Lab & Projects"], cta: "Go Pro", highlight: true },
  { name: "Business", price: "Custom", desc: "For teams & enterprises", features: ["Everything in Pro", "Workforce dashboard", "Assign & compliance", "White-label & domain", "Dedicated success manager"], cta: "Talk to sales", highlight: false },
];

const FAQ = [
  { q: "How is LaneX different from a video course platform?", a: "There are no static videos. Your personal mentor teaches you interactively, adapts every lesson, quizzes you, and evolves with your progress until you truly master a topic." },
  { q: "What powers LaneX Academy?", a: "LaneX Academy is built on a shared engineering platform that we continuously improve behind the scenes — so learners get better lessons, sharper feedback and faster experiences over time without ever having to think about the underlying technology." },
  { q: "Do I get a real certificate?", a: "Yes. Every completed path issues a unique, publicly verifiable certificate with QR code that you can add to LinkedIn in one click." },
  { q: "Is there a free plan?", a: "Yes — the Free tier lets you experience the mentor and two starter paths. You can upgrade at any time." },
  { q: "Can my company use LaneX?", a: "Absolutely. The Business plan gives you a workforce dashboard, mandatory training, compliance tracking, and white-labeling." },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      {/* Top nav */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border bg-background/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <Link to="/" data-testid={NAV.logoLink} className="flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX Academy" className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_14px_rgba(0,238,199,0.45)]" />
            <span className="font-display text-lg font-black tracking-tighter">LaneX Academy</span>
          </Link>
          <div className="hidden items-center gap-6 md:flex">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground">Features</a>
            <a href="#paths" className="text-sm text-muted-foreground hover:text-foreground">Paths</a>
            <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground">Pricing</Link>
            <Link to="/about" data-testid="landing-about-link" className="text-sm text-muted-foreground hover:text-foreground">About</Link>
            <a href="#enterprise" className="text-sm text-muted-foreground hover:text-foreground">Enterprise</a>
            <a href="#faq" className="text-sm text-muted-foreground hover:text-foreground">FAQ</a>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Button data-testid={NAV.dashboardLink} onClick={() => navigate("/app/dashboard")} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">Open App</Button>
            ) : (
              <>
                <Button data-testid={NAV.loginBtn} variant="ghost" onClick={() => navigate("/login")} className="rounded-full font-mono text-xs uppercase tracking-widest hidden sm:inline-flex">Sign in</Button>
                <Button data-testid={NAV.signupBtn} onClick={() => navigate("/register")} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">Get started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="grid-pattern grid-pattern-fade absolute inset-0 opacity-60" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse-glow" />
                <span className="label-mono text-[0.65rem] text-primary">The future of learning · v1.0</span>
              </div>
              <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
                Your personal <br />
                <span className="gradient-cyan-lime">learning mentor,</span> <br />
                until you master it.
              </h1>
              <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
                LaneX Academy isn't a video-course platform. It's an intelligent learning experience that teaches,
                quizzes, remembers, and adapts — from Python to leadership — until you truly get it.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  data-testid={LANDING.heroCta}
                  size="lg"
                  className="rounded-full btn-neon px-7 font-mono text-xs uppercase tracking-[0.2em]"
                  onClick={() => navigate(user ? "/app/dashboard" : "/register")}
                >
                  Start learning free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  data-testid={LANDING.heroSecondaryCta}
                  size="lg"
                  variant="outline"
                  className="rounded-full px-7 font-mono text-xs uppercase tracking-[0.2em]"
                  onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                >
                  See how it works
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                <span className="label-mono">Trusted stack:</span>
                {PATHS_PREVIEW.slice(0, 6).map((p) => (
                  <span key={p} className="font-mono text-xs">{p}</span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="lg:col-span-5"
            >
              <div className="relative rounded-2xl border border-border bg-card/70 p-1 shadow-[0_20px_60px_-20px_rgba(0,229,255,0.35)]">
                <div className="rounded-xl border border-border bg-background p-5">
                  <div className="flex items-center gap-2 border-b border-border pb-3">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                    <span className="label-mono text-[0.6rem]">LaneX Mentor · Adaptive</span>
                  </div>
                  <div className="space-y-4 pt-4">
                    <div className="rounded-lg border border-border bg-muted/30 p-3">
                      <div className="label-mono mb-1 text-[0.6rem]">You</div>
                      <div className="text-sm">Explain gradient descent like I'm 10.</div>
                    </div>
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <div className="label-mono mb-1 text-[0.6rem] text-primary">LaneX Mentor</div>
                      <div className="text-sm leading-relaxed">
                        Imagine you're at the top of a foggy hill and want to reach the lowest valley.
                        You can't see far, so you feel with your feet and take a small step downhill.
                        Do that again and again — that's gradient descent. Want a real example next?
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {["Give another example", "Make it harder", "Test me"].map((t) => (
                        <span key={t} className="rounded-full border border-border bg-secondary px-3 py-1 font-mono text-xs">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" data-testid={LANDING.featuresSection} className="relative py-20 md:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="mb-14 max-w-3xl">
            <div className="label-mono mb-3 text-primary">// features</div>
            <h2 className="font-display text-3xl font-black tracking-tighter sm:text-4xl lg:text-5xl">
              A learning system that <em className="not-italic gradient-cyan-lime">teaches back</em>.
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3 md:auto-rows-fr">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`card-hover rounded-2xl border border-border bg-card p-6 ${i === 0 ? "md:col-span-2" : ""}`}
              >
                <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg ${f.tone === "primary" ? "bg-primary/10 text-primary border border-primary/30" : "bg-accent/10 text-accent border border-accent/30"}`}>
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl font-bold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Paths */}
      <section id="paths" className="relative py-20 md:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="label-mono mb-3 text-primary">// learning paths</div>
              <h2 className="font-display text-3xl font-black tracking-tighter sm:text-4xl">
                From code to career, guided by your mentor.
              </h2>
            </div>
            <div className="text-sm text-muted-foreground max-w-md">
              12+ curated paths at launch. Admins can create unlimited new paths tailored to any team or company.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {PATHS_PREVIEW.map((p) => (
              <span key={p} className="rounded-full border border-border bg-card px-4 py-1.5 font-mono text-xs hover:border-primary/50 transition">
                {p}
              </span>
            ))}
            <span className="rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-mono text-xs text-primary">
              + more
            </span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative py-20 md:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="mb-14 max-w-3xl">
            <div className="label-mono mb-3 text-primary">// how it works</div>
            <h2 className="font-display text-3xl font-black tracking-tighter sm:text-4xl">Three steps to mastery.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { n: "01", icon: GraduationCap, t: "Pick a path", d: "Choose from Python, AI, SQL, Cybersecurity, Leadership — or ask your mentor for a custom track." },
              { n: "02", icon: Sparkles, t: "Learn with your mentor", d: "Every lesson is personalised to you. Ask 'explain it like I'm 10' anytime. Get quizzed. Adapt." },
              { n: "03", icon: Rocket, t: "Earn & apply", d: "Finish, get certified, and use the Career Coach to write your CV, prep interviews, and land the role." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-border bg-card p-6">
                <div className="label-mono text-primary">{s.n}</div>
                <s.icon className="my-4 h-8 w-8 text-primary" />
                <h3 className="font-display text-xl font-bold">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" data-testid={LANDING.pricingSection} className="relative py-20 md:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="mb-14 max-w-3xl">
            <div className="label-mono mb-3 text-primary">// pricing</div>
            <h2 className="font-display text-3xl font-black tracking-tighter sm:text-4xl">Fair pricing. Serious learning.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {PRICING.map((p) => (
              <div
                key={p.name}
                className={`relative rounded-2xl border p-8 ${p.highlight ? "border-primary/50 bg-primary/5 shadow-[0_20px_60px_-20px_rgba(0,229,255,0.35)]" : "border-border bg-card"}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest text-primary-foreground">
                    Most popular
                  </div>
                )}
                <div className="label-mono">{p.name}</div>
                <div className="mt-3 flex items-end gap-1">
                  <div className="font-display text-4xl font-black tracking-tighter">{p.price}</div>
                  {p.tag && <div className="pb-1 text-sm text-muted-foreground">{p.tag}</div>}
                </div>
                <div className="mt-2 text-sm text-muted-foreground">{p.desc}</div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 text-primary" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className={`mt-8 w-full rounded-full font-mono text-xs uppercase tracking-widest ${p.highlight ? "btn-neon" : ""}`}
                  variant={p.highlight ? "default" : "outline"}
                  onClick={() => navigate(user ? "/app/dashboard" : "/register")}
                >
                  {p.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise */}
      <section id="enterprise" data-testid={LANDING.enterpriseSection} className="relative py-20 md:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <div className="label-mono mb-3 text-primary">// enterprise</div>
              <h2 className="font-display text-3xl font-black tracking-tighter sm:text-4xl">Upskill your entire workforce.</h2>
              <p className="mt-4 text-muted-foreground">
                Assign mandatory learning, track compliance, and give every employee their own personal mentor.
                White-label the domain, colours, and certificates for a seamless brand experience.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                {["Company dashboard & analytics", "Departments, roles & assignments", "White-label domain and branding", "Dedicated success manager"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{f}</li>
                ))}
              </ul>
              <Button className="mt-8 rounded-full btn-neon font-mono text-xs uppercase tracking-widest">Talk to sales</Button>
            </div>
            <div className="relative rounded-2xl border border-border bg-card p-6">
              <div className="mb-6 label-mono text-[0.6rem]">Live workforce dashboard · preview</div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { l: "Active learners", v: "1,284" },
                  { l: "Avg. mastery", v: "82%" },
                  { l: "Certificates", v: "947" },
                ].map((s) => (
                  <div key={s.l} className="rounded-xl border border-border bg-background p-4">
                    <div className="label-mono text-[0.55rem]">{s.l}</div>
                    <div className="mt-1 font-display text-2xl font-black">{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 h-32 rounded-xl border border-border bg-gradient-to-t from-primary/10 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid={LANDING.faqSection} className="relative py-20 md:py-28 border-t border-border">
        <div className="mx-auto max-w-3xl px-4 md:px-8">
          <div className="label-mono mb-3 text-primary">// faq</div>
          <h2 className="font-display text-3xl font-black tracking-tighter sm:text-4xl">Common questions.</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="text-left font-display text-lg">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section data-testid={LANDING.ctaSection} className="relative py-20 md:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <Zap className="mx-auto h-8 w-8 text-primary" />
          <h2 className="mt-4 font-display text-4xl font-black tracking-tighter sm:text-5xl">
            Learn faster. Learn deeper. <br /> <span className="gradient-cyan-lime">Learn until you master it.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Free forever plan. No credit card. Your personal mentor is one click away.
          </p>
          <Button
            size="lg"
            className="mt-8 rounded-full btn-neon px-10 font-mono text-xs uppercase tracking-[0.2em]"
            onClick={() => navigate(user ? "/app/dashboard" : "/register")}
          >
            Start learning free <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-4 md:flex-row md:items-center md:px-8">
          <div className="flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX Academy" className="h-8 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_12px_rgba(0,238,199,0.45)]" />
            <span className="font-display font-black">LaneX Academy</span>
          </div>
          <div className="label-mono">© 2026 LaneX Academy — Built for learners.</div>
        </div>
      </footer>
    </div>
  );
}
